import type { SavedCircuit } from './api.js';
import { circuitDisplayTitle } from './circuitDisplay.js';
import { createCircuitPreviewCard, paintCircuitPreviewCard } from '../ui/circuitPreviewCard.js';

/** Nettoyages des vignettes 3D en cours, par conteneur — voir `renderList`. */
const cleanupsByContainer = new WeakMap<HTMLElement, Array<() => void>>();

/**
 * Démonte les vignettes 3D de l'affichage précédent avant de vider le DOM :
 * une fois les cartes détachées, l'IntersectionObserver qui les gère peut
 * tarder à signaler leur sortie de vue, ce qui laisserait leurs contextes
 * WebGL ouverts. À appeler avant tout remplacement de `container.innerHTML`.
 */
function disposeListPreviews(container: HTMLElement): void {
  cleanupsByContainer.get(container)?.forEach((cleanup) => cleanup());
  cleanupsByContainer.delete(container);
}

export function renderList(
  container: HTMLElement,
  circuits: SavedCircuit[],
  onSelect: (id: number) => void,
  selectedId: number | null = null
): void {
  disposeListPreviews(container);
  const cleanups: Array<() => void> = [];
  cleanupsByContainer.set(container, cleanups);

  container.innerHTML = '';

  if (circuits.length === 0) {
    container.innerHTML = `
      <div class="circuits-empty">
        <span class="circuits-empty-icon" aria-hidden="true">📋</span>
        <p>Aucun circuit enregistré.</p>
        <p class="circuits-empty-hint">Générez un circuit depuis l’onglet Jeu pour le retrouver ici.</p>
      </div>`;
    return;
  }

  const list = document.createElement('div');
  list.className = 'circuits-list';

  for (const c of circuits) {
    const date = new Date(c.created_at);
    const dateStr = date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

    const card = createCircuitPreviewCard({
      layout: 'row',
      selected: selectedId !== null && c.id === selectedId,
      lines: {
        title: circuitDisplayTitle(c),
        date: dateStr,
        meta: `${c.params.numStraights} droites · ${c.params.numTurns} virages · ~${c.params.approximateCells} cases`,
      },
      onActivate: () => onSelect(c.id),
    });
    card.setAttribute('data-id', String(c.id));
    cleanups.push(paintCircuitPreviewCard(card, c.circuit, container, c.name));
    list.appendChild(card);
  }

  container.appendChild(list);
}

export function setListLoading(container: HTMLElement, loading: boolean): void {
  if (loading) {
    disposeListPreviews(container);
    container.innerHTML =
      '<div class="circuits-loading"><span class="spinner"></span><p>Chargement des circuits…</p></div>';
  }
}

export function setListError(container: HTMLElement, message: string): void {
  disposeListPreviews(container);
  container.innerHTML = `
    <div class="circuits-error">
      <span class="circuits-error-icon" aria-hidden="true">⚠️</span>
      <p>${escapeHtml(message)}</p>
    </div>`;
}

function escapeHtml(s: string): string {
  const div = document.createElement('div');
  div.textContent = s;
  return div.innerHTML;
}
